import React, { useState, useEffect } from 'react';
import { X, ChevronDown, ChevronRight } from 'lucide-react';
import { serviceRatesService } from '../services/serviceRates.service';
import localeConfig from '../config/locale.config';

const EditServiceRateRow = ({ 
    serviceRate, 
    onCancelEdit, 
    onSaveEdit, 
    loading 
}) => {
    const [editedRate, setEditedRate] = useState({ ...serviceRate });
    const [serviceTypes, setServiceTypes] = useState([]);
    const [serviceSubtypes, setServiceSubtypes] = useState({});
    const [loadingServiceTypes, setLoadingServiceTypes] = useState(false);
    const [expanded, setExpanded] = useState(true);

    useEffect(() => {
        loadServiceTypes();
        if (serviceRate.service_type_code) {
            loadServiceSubtypes(serviceRate.service_type_code);
        }
    }, [serviceRate.service_type_code]);

    const loadServiceTypes = async () => {
        try {
            setLoadingServiceTypes(true);
            const types = await serviceRatesService.getAllServiceTypes();
            setServiceTypes(types);
        } catch (error) {
            console.error('Error loading service types:', error);
        } finally {
            setLoadingServiceTypes(false);
        }
    };

    const loadServiceSubtypes = async (serviceTypeCode) => {
        if (!serviceTypeCode) {
            setServiceSubtypes({});
            return;
        }

        try {
            const subtypes = await serviceRatesService.getServiceSubtypes(serviceTypeCode);
            setServiceSubtypes(prev => ({
                ...prev,
                [serviceTypeCode]: subtypes
            }));
        } catch (error) {
            console.error('Error loading service subtypes:', error);
        }
    };

    const handleServiceTypeChange = (value) => {
        setEditedRate(prev => ({
            ...prev,
            service_type_code: value,
            service_subtype_code: '' // Clear subtype when service type changes
        }));
        // Load subtypes for the new service type
        if (value) {
            loadServiceSubtypes(value);
        }
    };

    const handleFieldChange = (field, value) => {
        setEditedRate(prev => ({
            ...prev,
            [field]: value
        }));
    };

    const handleSave = () => {
        // Find changed fields by comparing with original
        const changedFields = {};
        Object.keys(editedRate).forEach(key => {
            if (editedRate[key] !== serviceRate[key]) {
                changedFields[key] = editedRate[key];
            }
        });

        if (Object.keys(changedFields).length === 0) {
            alert('No changes detected');
            return;
        }

        onSaveEdit(changedFields);
    };

    const timeSlots = [
        { value: 'DAY', label: 'Day (6 AM - 6 PM)' },
        { value: 'NIGHT', label: 'Night (6 PM - 6 AM)' }
    ];

    return (
        <div className="border border-gray-200 rounded-lg mb-4 overflow-hidden">
            {/* Header */}
            <div 
                className="bg-blue-50 px-4 py-2.5 cursor-pointer hover:bg-blue-100 transition-colors border-b border-blue-100"
                onClick={() => setExpanded(!expanded)}
            >
                <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-3">
                        {expanded ? (
                            <ChevronDown className="w-4 h-4 text-gray-500" />
                        ) : (
                            <ChevronRight className="w-4 h-4 text-gray-500" />
                        )}
                        <h3 className="text-base font-medium text-gray-900">
                            Edit Service Rate
                        </h3>
                        <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                            Editing
                        </span>
                    </div>
                    <div className="flex items-center space-x-2">
                        <button
                            onClick={onCancelEdit}
                            className="text-gray-400 hover:text-gray-600 p-1"
                        >
                            <X className="w-4 h-4" />
                        </button>
                    </div>
                </div>
            </div>

            {/* Content */}
            {expanded && (
                <div className="p-4 bg-white">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        {/* Service Type */}
                        <div>
                            <label className="block text-xs font-medium text-gray-700 mb-1">
                                Service Type *
                            </label>
                            <select
                                value={editedRate.service_type_code}
                                onChange={(e) => handleServiceTypeChange(e.target.value)}
                                className="w-full px-2.5 py-1.5 text-sm border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                                disabled={loadingServiceTypes}
                                required
                            >
                                <option value="">Select Service Type</option>
                                {serviceTypes.map(type => (
                                    <option key={type.service_type_code} value={type.service_type_code}>
                                        {type.service_type_name}
                                    </option>
                                ))}
                            </select>
                        </div>

                        {/* Service Subtype */}
                        <div>
                            <label className="block text-xs font-medium text-gray-700 mb-1">
                                Service Subtype
                            </label>
                            <select
                                value={editedRate.service_subtype_code}
                                onChange={(e) => handleFieldChange('service_subtype_code', e.target.value)}
                                className="w-full px-2.5 py-1.5 text-sm border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                                disabled={!editedRate.service_type_code}
                            >
                                <option value="">Select Subtype (Optional)</option>
                                {serviceSubtypes[editedRate.service_type_code]?.map(subtype => (
                                    <option key={subtype.subtype_code} value={subtype.subtype_code}>
                                        {subtype.subtype_name}
                                    </option>
                                ))}
                            </select>
                        </div>

                        {/* Time Slot */}
                        <div>
                            <label className="block text-xs font-medium text-gray-700 mb-1">
                                Time Slot *
                            </label>
                            <select
                                value={editedRate.time_slot}
                                onChange={(e) => handleFieldChange('time_slot', e.target.value)}
                                className="w-full px-2.5 py-1.5 text-sm border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                                required
                            >
                                {timeSlots.map(slot => (
                                    <option key={slot.value} value={slot.value}>
                                        {slot.label}
                                    </option>
                                ))}
                            </select>
                        </div>

                        {/* Time Range */}
                        <div className="grid grid-cols-2 gap-2">
                            <div>
                                <label className="block text-xs font-medium text-gray-700 mb-1">
                                    Start Time *
                                </label>
                                <input
                                    type="time"
                                    value={editedRate.time_slot_start}
                                    onChange={(e) => handleFieldChange('time_slot_start', e.target.value)}
                                    className="w-full px-2.5 py-1.5 text-sm border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                                    required
                                />
                            </div>
                            <div>
                                <label className="block text-xs font-medium text-gray-700 mb-1">
                                    End Time *
                                </label>
                                <input
                                    type="time"
                                    value={editedRate.time_slot_end}
                                    onChange={(e) => handleFieldChange('time_slot_end', e.target.value)}
                                    className="w-full px-2.5 py-1.5 text-sm border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                                    required
                                />
                            </div>
                        </div>

                        {/* Base Service Rate */}
                        <div>
                            <label className="block text-xs font-medium text-gray-700 mb-1">
                                Base Service Rate ({localeConfig.currency.symbol}) *
                            </label>
                            <input
                                type="number"
                                step="0.01"
                                value={editedRate.base_service_rate}
                                onChange={(e) => handleFieldChange('base_service_rate', e.target.value)}
                                className="w-full px-2.5 py-1.5 text-sm border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                                placeholder="0.00"
                                required
                            />
                        </div>

                        {/* Base Max KMs */}
                        <div>
                            <label className="block text-xs font-medium text-gray-700 mb-1">
                                Base Max KMs *
                            </label>
                            <input
                                type="number"
                                step="0.01"
                                value={editedRate.base_max_kms}
                                onChange={(e) => handleFieldChange('base_max_kms', e.target.value)}
                                className="w-full px-2.5 py-1.5 text-sm border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                                placeholder="0.00"
                                required
                            />
                        </div>

                        {/* Additional Rate per KM */}
                        <div>
                            <label className="block text-xs font-medium text-gray-700 mb-1">
                                Additional Rate per KM ({localeConfig.currency.symbol}) *
                            </label>
                            <input
                                type="number"
                                step="0.01"
                                value={editedRate.additional_rate_per_km}
                                onChange={(e) => handleFieldChange('additional_rate_per_km', e.target.value)}
                                className="w-full px-2.5 py-1.5 text-sm border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                                placeholder="0.00"
                                required
                            />
                        </div>

                        {/* Fixed Rate */}
                        <div>
                            <label className="block text-xs font-medium text-gray-700 mb-1">
                                Fixed Rate ({localeConfig.currency.symbol})
                            </label>
                            <input
                                type="number"
                                step="0.01"
                                value={editedRate.fixed_rate}
                                onChange={(e) => handleFieldChange('fixed_rate', e.target.value)}
                                className="w-full px-2.5 py-1.5 text-sm border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                                placeholder="0.00"
                            />
                        </div>

                        {/* Cancel Charges - Upto Base KMs */}
                        <div>
                            <label className="block text-xs font-medium text-gray-700 mb-1">
                                Cancel Charges - Upto Base KMs ({localeConfig.currency.symbol}) *
                            </label>
                            <input
                                type="number"
                                step="0.01"
                                value={editedRate.cancel_charges_per_km_upto_base_kms}
                                onChange={(e) => handleFieldChange('cancel_charges_per_km_upto_base_kms', e.target.value)}
                                className="w-full px-2.5 py-1.5 text-sm border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                                placeholder="0.00"
                                required
                            />
                        </div>

                        {/* Cancel Charges - Above Base KMs */}
                        <div>
                            <label className="block text-xs font-medium text-gray-700 mb-1">
                                Cancel Charges - Above Base KMs ({localeConfig.currency.symbol}) *
                            </label>
                            <input
                                type="number"
                                step="0.01"
                                value={editedRate.cancel_charges_per_km_above_base_kms}
                                onChange={(e) => handleFieldChange('cancel_charges_per_km_above_base_kms', e.target.value)}
                                className="w-full px-2.5 py-1.5 text-sm border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                                placeholder="0.00"
                                required
                            />
                        </div>

                        {/* Effective From */}
                        <div>
                            <label className="block text-xs font-medium text-gray-700 mb-1">
                                Effective From *
                            </label>
                            <input
                                type="date"
                                value={editedRate.effective_from}
                                onChange={(e) => handleFieldChange('effective_from', e.target.value)}
                                className="w-full px-2.5 py-1.5 text-sm border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                                required
                            />
                        </div>

                        {/* Effective To */}
                        <div>
                            <label className="block text-xs font-medium text-gray-700 mb-1">
                                Effective To
                            </label>
                            <input
                                type="date"
                                value={editedRate.effective_to}
                                onChange={(e) => handleFieldChange('effective_to', e.target.value)}
                                className="w-full px-2.5 py-1.5 text-sm border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                            />
                        </div>
                    </div>

                    {/* Action Buttons */}
                    <div className="flex justify-end space-x-3 mt-6 pt-4 border-t border-gray-200">
                        <button
                            type="button"
                            onClick={onCancelEdit}
                            disabled={loading}
                            className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 border border-gray-300 rounded-md hover:bg-gray-200 transition-colors disabled:opacity-50"
                        >
                            Cancel
                        </button>
                        <button
                            type="button"
                            onClick={handleSave}
                            disabled={loading}
                            className="px-4 py-2 text-sm font-medium text-white bg-blue-600 border border-blue-600 rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 transition-colors disabled:opacity-50"
                        >
                            {loading ? 'Saving...' : 'Save Changes'}
                        </button>
                    </div>
                </div>
            )}
        </div>
    );
};

export default EditServiceRateRow; 